import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const getDominantColor = async (imageUrl, options = {}) => {
  const {
    excludeWhite = true,
    excludeBlack = true,
    grayThreshold = 30,
    whiteTolerance = 250,
    blackTolerance = 20,
    sampleSize = 50
  } = options;

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = sampleSize;
      canvas.height = sampleSize;
      ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
      
      const imageData = ctx.getImageData(0, 0, sampleSize, sampleSize).data;
      const colorMap = new Map();

      for (let i = 0; i < imageData.length; i += 4) {
        const [r, g, b, a] = [imageData[i], imageData[i + 1], imageData[i + 2], imageData[i + 3]];
        
        if (a < 255) continue;
        if (excludeWhite && r > whiteTolerance && g > whiteTolerance && b > whiteTolerance) continue;
        if (excludeBlack && r < blackTolerance && g < blackTolerance && b < blackTolerance) continue;
        if (Math.abs(r - g) < grayThreshold && Math.abs(g - b) < grayThreshold && Math.abs(r - b) < grayThreshold) continue;

        const colorKey = `${r},${g},${b}`;
        colorMap.set(colorKey, (colorMap.get(colorKey) || 0) + 1);
      }

      let maxCount = 0;
      let dominantColor = null;

      for (const [color, count] of colorMap.entries()) {
        if (count > maxCount) {
          maxCount = count;
          dominantColor = color;
        }
      }

      if (!dominantColor) {
        resolve(null);
        return;
      }

      const [r, g, b] = dominantColor.split(',').map(Number);
      resolve({
        rgb: [r, g, b],
        rgbString: `rgb(${r}, ${g}, ${b})`,
        hex: `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
      });
    };

    img.onerror = () => resolve(null);
    img.src = imageUrl;
  });
};

const GameCard = ({ game, onClick, dominantColors }) => {
  const [rotation, setRotation] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const xRotate = ((y - rect.height / 2) / rect.height) * 20;
    const yRotate = ((x - rect.width / 2) / rect.width) * 20;
    
    setRotation({ x: -xRotate, y: yRotate });
  };

  const handleMouseLeave = () => {
    setRotation({ x: 0, y: 0 });
  };

  const cardStyle = {
    transform: `perspective(1000px) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) scale(${rotation.x || rotation.y ? '1.05' : '1'})`,
    boxShadow: dominantColors ? `
      0 0 10px ${dominantColors[0]},
      0 5px 15px ${dominantColors[1] || dominantColors[0]},
      0 0 30px ${dominantColors[0]}
    ` : '0 0 15px rgba(255, 255, 255, 0.2)'
  };

  return (
    <div
      onClick={() => onClick(game)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative w-[280px] h-[180px] rounded-lg overflow-hidden cursor-pointer bg-black transform-gpu transition-all duration-300 hover:shadow-2xl group"
      style={cardStyle}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <img 
        src={game.image} 
        alt={game.name} 
        className="w-full h-full object-cover"
      />
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/70 to-transparent transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
        <h3 className="text-white font-semibold tracking-wide text-shadow">{game.name}</h3>
      </div>
    </div>
  );
};

const UnrealDB = () => {
    const [games, setGames] = useState([]);
    const navigate = useNavigate(); // Hook for navigation
  
    useEffect(() => {
      const fetchGames = async () => {
        try {
          const gamesUrl = `https://cdn.spookytools.com/Games/db.json?v=${Date.now()}`;
          const response = await fetch(gamesUrl);
          const data = await response.json();
          
          const gamesWithColors = await Promise.all(
            data.Games.map(async (game) => {
              const topColor = await getDominantColor(game.ImageUrl, { sampleSize: 25 });
              const bottomColor = await getDominantColor(game.ImageUrl, {
                sampleSize: 25,
                excludeWhite: false,
                excludeBlack: false
              });
              
              return {
                name: game.Name,
                image: game.ImageUrl,
                cdnUrl: game.JsonFiles,
                dominantColors: [topColor?.rgbString, bottomColor?.rgbString].filter(Boolean)
              };
            })
          );
          
          setGames(gamesWithColors);
        } catch (error) {
          console.error('Error fetching games:', error);
        }
      };
  
      fetchGames();
    }, []);
  
    const handleGameClick = (game) => {
      navigate('/sdk-view', { 
        state: { 
          jsonfiles: JSON.stringify(game.cdnUrl), // Assuming cdnUrl is an array or object of JSON files
          gamename: game.name 
        }
      });
    };
  
    return (
      <div className="w-full min-h-screen pt-[15vh] overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-mono text-center mb-12 text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-rose-500 drop-shadow-[0_0_10px_rgba(220,38,38,0.5)]">
            Game SDK's_
          </h1>
          
          <div className="flex flex-wrap justify-center gap-8 mb-12">
            {games.map((game, index) => (
              <GameCard
                key={index}
                game={game}
                onClick={handleGameClick}
                dominantColors={game.dominantColors}
              />
            ))}
          </div>
  
          <Link 
            to="/"
            className="fixed bottom-4 sm:bottom-8 left-4 sm:left-8 text-gray-400 text-xs sm:text-sm hover:text-red-400 transition-colors"
          >
            Back to Main Menu
          </Link>
        </div>
      </div>
    );
};
  
export default UnrealDB;